/*
 * A11y (Accessibility) style rules
 * These are used to mark up areas on the site that should only be readable
 * when using a screen reader.
 */
import {StyleSheet} from "aphrodite";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";

// Make content only visible to screen readers.
// Both collegeboard.org and Bootstrap 3 use this exact implementation.
// Note: This should be used within Aphrodite
export const srOnly = {
    border: 0,
    clip: "rect(0,0,0,0)",
    height: "1px",
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    width: 1,
} as const satisfies StyleType;

export const a11yStyles = StyleSheet.create({
    // Disable transitions when the user has requested reduced motion.
    // NOTE: This needs to be accompanied by `prefersReducedMotion` in order to
    // work.
    prefersReducedMotion: {
        animation: "none",
        transition: "none",
    },

    srOnly,

    // A "Skip to main content" link shown only when active (accessible by first
    // press of the tab key) that links directly to #page-content
    skipToMain: {
        // We want to hide it from view just like a screen reader element, but
        // we will later overrule some properties to make it visible
        ...srOnly,

        backgroundColor: semanticColor.surface.primary,
        // TODO: (WB-1914) update to newer WB size tokens
        insetInlineStart: sizing.size_640,
        lineHeight: "4.5rem",
        padding: `0 ${sizing.size_100}`,
        top: sizing.size_640,

        ":focus": {
            clip: "auto",
            height: "auto",
            width: "auto",
            // The skipToMain link is the very first element in the DOM, and it's
            // absolutely positioned, which means it's behind the standard content
            // on the page. We need it to become visible on focus so keyboard users
            // can skip to the main content easily. This also, unfortunately,
            // means we need to set a very high z-index to make sure it's definitely
            // visible on focus.
            zIndex: 9999,
        },

        ":active": {
            clip: "auto",
            height: "auto",
            width: "auto",
        },
    },
});
